<template>
    <div :class="[item.key === selected?.key ? 'border-royal-blue-500 ring-2 ring-royal-blue-500' : 'border-gray-300', 'relative block cursor-pointer rounded-lg border bg-white px-6 py-4 shadow-xs focus:outline-hidden sm:flex sm:justify-between']">
        <span class="flex items-center">
            <span class="flex flex-col text-sm">
                <span class="font-medium text-gray-900">
                    {{ item.title }}
                    <template v-if="!(blackFridayUpgrade && blackFridayUpgrade.can_upgrade && period === 1)">
                        <span v-if="item.key == subscription?.key && period === 1" class="ml-2 inline-flex items-center rounded-full bg-royal-blue-50 px-1.5 py-0.5 text-xs font-medium text-royal-blue-500 ring-1 ring-royal-blue-700/10 ring-inset">{{ $t('subscription_plan.current') }}</span>
                        <span v-if="!(item.key == subscription?.key && period === 1) && item.selected" class="ml-2 inline-flex items-center rounded-full bg-royal-blue-50 px-1.5 py-0.5 text-xs font-medium text-royal-blue-500 ring-1 ring-royal-blue-700/10 ring-inset">🔥 {{ $t('subscription_plan.most_popular') }}</span>
                    </template>
                    <span v-if="hasFaceRecognition" class="ml-2 inline-flex items-center rounded-full bg-royal-blue-50 px-1.5 py-0.5 text-xs font-medium text-royal-blue-500 ring-1 ring-royal-blue-700/10 ring-inset">✨ {{ $t('subscription_plan.face_recognition') }}</span>
                    <span v-if="blackFridayUpgrade && blackFridayUpgrade.can_upgrade && period === 1" class="ml-2 mt-1 sm:mt-0 inline-flex items-center rounded-full bg-gradient-to-r from-purple-50 to-pink-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-purple-200 ring-inset">
                        <span class="mr-1">⚡️</span>
                        <span class="hidden sm:inline">{{ $t('subscription.black_friday.discount') }}</span>
                        <span class="sm:hidden">{{ $t('subscription.black_friday.discount_short') }}</span>
                        <template v-if="hasBonusDays">
                            <span class="mx-1">+</span>
                            <span>🎁 {{ $t('subscription.black_friday.bonus_days', {days: blackFridayUpgrade.bonus_days}) }}</span>
                        </template>
                    </span>
                </span>
                <span class="text-gray-500">
                    <span class="block sm:inline">{{ Math.round(item.limit / (1024 * 1024 * 1024) * 100) / 100 }} {{ $t('subscription_plan.gb_storage') }}</span>
                    {{ ' ' }}
                    <span v-if="item.key !== 'free'" class="hidden sm:mx-1 sm:inline" aria-hidden="true">&middot;</span>
                    {{ ' ' }}
                    <span v-if="item.key !== 'free'" class="block sm:inline">{{ $t('subscription_plan.up_to_video') }}</span>
                    {{ ' ' }}
                    <span v-if="item.key !== 'free'" class="hidden sm:mx-1 sm:inline" aria-hidden="true">&middot;</span>
                    {{ ' ' }}
                    <span v-if="item.key !== 'free'" class="block sm:inline">{{ $t('subscription_plan.unlimited_storage') }}</span>

                    <span v-if="item.key === 'free'" class="hidden sm:mx-1 sm:inline" aria-hidden="true">&middot;</span>
                    {{ ' ' }}
                    <span v-if="item.key === 'free'" class="block sm:inline">{{ $t('subscription_plan.gallery_storage_60_days') }}</span>
                </span>
            </span>
        </span>
        <span class="mt-2 flex text-base sm:text-sm sm:mt-0 sm:ml-4 flex-col sm:justify-center sm:text-right">
            <span class="font-medium text-gray-900">
                <template v-if="blackFridayUpgrade && blackFridayUpgrade.can_upgrade && period === 1">
                    <div class="flex flex-col sm:flex-row sm:items-center gap-1">
                        <div class="flex items-center gap-1.5 sm:gap-1">
                            <s class="text-gray-400 text-sm sm:text-xs">{{ formatPrice(originalPricePerMonth) }} {{ currencySymbol }}</s>
                            <span class="text-purple-600 font-bold text-base sm:text-sm">{{ formatPrice(pricePerMonth) }} {{ currencySymbol }}</span>
                        </div>
                        <span class="text-gray-500 text-sm sm:text-sm">/ {{ $t('subscription_plan.month') }}</span>
                    </div>
                </template>
                <template v-else>
                    {{ formatPrice(pricePerMonth) }} {{ currencySymbol }}
                    <span class="ml-1 text-gray-500 sm:ml-0">/ {{ $t('subscription_plan.month') }}</span>
                </template>
            </span>
            <span v-if="period == 12" class="text-gray-500 text-sm sm:text-sm">{{ formatPrice(planPriceForPeriod) }} {{ currencySymbol }} / {{ $t('subscription_plan.year') }}</span>
        </span>
        <span :class="[item.key === subscription?.key ? 'border' : 'border-2', item.key === selected?.key ? 'border-royal-blue-500' : 'border-transparent', 'pointer-events-none absolute -inset-px rounded-lg']" aria-hidden="true" />
    </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import type {PropType} from "vue";
import {mapGetters} from "vuex";
import type {Subscription, SubscriptionPlan} from "@/types/UserType";

export default defineComponent({
    name: "SubscriptionPlanComponent",

    props: {
        item: {
            type: Object as PropType<SubscriptionPlan>,
            required: true,
        },
        period: {
            type: Number,
            default: 1,
        },
        subscription: {
            type: Object as PropType<Subscription | null>,
            default: null,
        },
        selected: {
            type: Object as PropType<SubscriptionPlan | null>,
            default: null,
        },
        blackFridayUpgrade: {
            type: Object,
            default: null
        }
    },

    data: function () {
        return {

        };
    },

    computed: {
        ...mapGetters({
            user: "user/user",
        }),

        userCurrency(): 'UAH' | 'EUR' {
            return this.user?.currency || 'UAH';
        },

        currencySymbol(): string {
            return this.userCurrency === 'EUR' ? this.$t('subscription_plan.eur') : this.$t('subscription_plan.uah');
        },

        safePeriod(): number {
            const value = typeof this.period === 'number' ? this.period : 1;
            return value > 0 ? value : 1;
        },

        planPriceForPeriod(): number {
            if(!this.item || this.item.key === 'free') {
                return 0;
            }

            const periodKey = this.safePeriod.toString();

            if(this.item.periods && this.item.periods[periodKey]) {
                const periodData = this.item.periods[periodKey] || {};
                return this.userCurrency === 'EUR' ? (periodData.eur || 0) : (periodData.uah || 0);
            }

            // Fallback to base price
            return this.userCurrency === 'EUR' ? (this.item.price_eur || 0) : (this.item.price || 0);
        },

        originalPricePerMonth(): number {
            if(!this.item || this.item.key === 'free') {
                return 0;
            }
            const basePrice = this.userCurrency === 'EUR' ? (this.item.price_eur || 0) : (this.item.price || 0);
            return basePrice;
        },

        pricePerMonth(): number {
            if(!this.item || this.item.key === 'free') {
                return 0;
            }
            
            // Якщо є Black Friday апгрейд, застосовуємо знижку 50%
            if(this.blackFridayUpgrade && this.blackFridayUpgrade.can_upgrade && this.period === 1) {
                return Math.floor(this.originalPricePerMonth * 0.5);
            }
            
            return this.safePeriod === 0 ? 0 : this.planPriceForPeriod / this.safePeriod;
        },

        discount(): number {
            if(!this.item || this.item.key === 'free' || (this.item.price || 0) == 0) {
                return 0;
            }

            const basePrice = this.userCurrency === 'EUR' ? (this.item.price_eur || 0) : (this.item.price || 0);
            
            if(this.pricePerMonth == basePrice) {
                return 0;
            }

            return parseInt((100 - ((this.pricePerMonth / basePrice) * 100)).toString());
        },

        hasBonusDays(): boolean {
            return Boolean(this.blackFridayUpgrade && Number(this.blackFridayUpgrade.bonus_days) > 0);
        },

        hasFaceRecognition(): boolean {
            return Boolean(this.item?.features?.face_recognition);
        },

        planPriceForYear(): number {
            if(!this.item || this.item.key === "free") {
                return 0;
            }

            if(this.item.periods && this.item.periods['12']) {
                const yearData = this.item.periods['12'];
                return this.userCurrency === 'EUR' ? (yearData.eur || 0) : (yearData.uah || 0);
            }

            return 0;
        }
    },

    methods: {
        formatPrice(price: number): string {
            if(price === 0) {
                return '0';
            }
            
            // Both EUR and UAH prices are stored in smallest units (cents/kopecks)
            // So we need to divide by 100 for both currencies
            const formattedPrice = price / 100;
            
            return Math.round(formattedPrice).toString();
        }
    },

    mounted() {
        console.log('SubscriptionPlanComponent mounted:', {
            planKey: this.item?.key,
            blackFridayUpgrade: this.blackFridayUpgrade
        });
    }
})
</script>

<style lang="scss" scoped>
.card {
    &.selected-plan {
        outline: 2px solid #0d6efd!important;
        box-shadow: 0 2px 4px rgb(0 0 0 / 6%);
    }
}
</style>